import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ChevronUp, ListChecks, Sparkles, ArrowRight, ShieldCheck, Activity, Timer } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/app/AppShell";
import { TaskRow, TaskForm, todayISO, scheduledToday } from "@/components/app/TaskUI";
import Heatmap from "@/components/app/Heatmap";
import { createTask, deleteTask, getMatches, getTasks, getWorkspace, importWorkspaceTask, toggleTask } from "@/lib/api";
import { getUser } from "@/lib/storage";

export default function Tasks() {
  const navigate = useNavigate();
  const [showAdd, setShowAdd] = useState(false);
  const [view, setView] = useState("today");
  const [tasks, setTasks] = useState([]);
  const [completionMap, setCompletionMap] = useState({});
  const [active, setActive] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = getUser();
  const entitlements = user?.entitlements || {};
  const isPaidTier = ["pro", "winter_arc"].includes(user?.subscriptionTier);
  const configuredRecurringLimit = entitlements.recurringTaskLimit ?? entitlements.recurring_task_limit;
  const recurringLimit = isPaidTier ? null : (configuredRecurringLimit ?? 5);
  const stakesEnabled = Boolean(entitlements.partnershipStakes ?? entitlements.partnership_stakes);

  const load = async () => {
    setLoading(true);
    try {
      const [taskResponse, matchResponse] = await Promise.all([getTasks(), getMatches()]);
      setTasks(taskResponse.tasks || []);
      setCompletionMap(taskResponse.heatmap || {});
      setActive(matchResponse.activePartnership || null);
      if (matchResponse.activePartnership) {
        setWorkspace(await getWorkspace(matchResponse.activePartnership.id));
      } else {
        setWorkspace(null);
      }
    } catch (error) {
      toast("Could not load tasks.", { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const partner = workspace?.partner?.profile || null;
  const importedSourceIds = new Set((workspace?.tasks || []).map((t) => t.sourceId).filter(Boolean));

  const scheduled = tasks.filter(scheduledToday);
  const doneToday = scheduled.filter((t) => (t.completions || []).includes(todayISO()));
  const submit = async (form) => {
    if (form.type === "recurring" && recurringLimit != null && tasks.filter((task) => task.type === "recurring").length >= recurringLimit) {
      toast("Recurring task limit reached.", { description: `Free includes ${recurringLimit} recurring tasks. One-time tasks are always unlimited.` });
      return;
    }
    try {
      const task = await createTask(form);
      setTasks((items) => [task, ...items]);
      setShowAdd(false);
      toast("Task added.", { description: partner ? "Import it into your partnership if you want it visible there too." : null });
    } catch (error) {
      toast("Could not add task.", { description: error.message });
    }
  };
  const tog = async (id) => {
    try {
      const previous = tasks.find((task) => task.id === id);
      const task = await toggleTask(id);
      setTasks((items) => items.map((item) => item.id === id ? task : item));
      setCompletionMap((current) => updateHeatmap(current, previous, task));
    } catch (error) { toast("Could not update task.", { description: error.message }); }
  };
  const del = async (id) => {
    try {
      await deleteTask(id);
      setTasks((items) => items.filter((task) => task.id !== id));
      toast("Task deleted.");
    } catch (error) { toast("Could not delete task.", { description: error.message }); }
  };
  const importOne = async (id) => {
    if (!active) { toast("You have no active partnership."); return; }
    try {
      await importWorkspaceTask(active.id, id);
      setWorkspace(await getWorkspace(active.id));
      toast("Task imported to workspace.", { description: `${partner?.fullName?.split(" ")[0] || "Your partner"} can now see it.` });
    } catch (error) { toast("Could not import task.", { description: error.message }); }
  };
  const importAll = async () => {
    if (!active) return;
    const toImport = tasks.filter((t) => !importedSourceIds.has(t.id));
    try {
      await Promise.all(toImport.map((task) => importWorkspaceTask(active.id, task.id)));
      setWorkspace(await getWorkspace(active.id));
      toast(`${toImport.length} task${toImport.length === 1 ? "" : "s"} imported.`);
    } catch (error) { toast("Could not import all tasks.", { description: error.message }); }
  };

  const list = view === "today" ? scheduled : tasks;
  const canUseWorkspace = Boolean(workspace?.capabilities?.fullWorkspace);

  return (
    <AppShell>
      <div className="px-6 sm:px-10 py-10 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="overline mb-3">Tasks</div>
            <h1 className="font-serif-editorial font-light tracking-tighter text-4xl sm:text-5xl leading-[1.05]">
              Your daily <em className="italic text-[#F59E0B]">operating rhythm.</em>
            </h1>
            <p className="mt-3 text-[#A1A1AA] font-light max-w-xl">
              Build habits that work even without a partner. Import them into your workspace when you have one, and your partner can see them too.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatPill label="Today" value={`${doneToday.length}/${scheduled.length}`} />
            <StatPill label="All tasks" value={tasks.length} />
          </div>
        </div>

        {/* Layout */}
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div>
            <div className="hm-card p-6 rounded-md">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <ListChecks className="w-4 h-4 text-[#F59E0B]" strokeWidth={1.5} />
                  <h2 className="font-serif-editorial text-2xl">Task board</h2>
                </div>
                <div className="flex items-center gap-1 border border-white/10 rounded-md p-1 text-xs">
                  {[
                    { k: "today", label: "Today" }, { k: "all", label: "All tasks" },
                  ].map((v) => (
                    <button
                      key={v.k} onClick={() => setView(v.k)}
                      data-testid={`tasks-view-${v.k}`}
                      className={`px-3 py-1 rounded-sm ${view === v.k ? "bg-[#F59E0B] text-black" : "text-[#A1A1AA]"}`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <button
                  onClick={() => setShowAdd((s) => !s)}
                  data-testid="tasks-toggle-add"
                  className="hm-btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs"
                >
                  {showAdd ? <ChevronUp className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  {showAdd ? "Close" : "New task"}
                </button>
                <Link to="/tools/focus" className="hm-btn-ghost inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs" data-testid="tasks-focus-room">
                  <Timer className="h-4 w-4" /> Focus room
                </Link>
                {active && canUseWorkspace && tasks.length > 0 && (
                  <button
                    onClick={importAll}
                    data-testid="tasks-import-all"
                    className="hm-btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs"
                    disabled={tasks.every((t) => importedSourceIds.has(t.id))}
                  >
                    <Activity className="w-4 h-4" /> Import all to workspace
                  </button>
                )}
              </div>

              <div className="text-xs text-[#A1A1AA] mb-4">
                {recurringLimit == null ? (
                  <>Recurring tasks: <span className="text-[#F59E0B]">Unlimited</span> on {user?.subscriptionTier === "winter_arc" ? "Winter Arc" : "Pro"} · one-time tasks are unlimited</>
                ) : (
                  <>Recurring tasks: <span className="text-[#F59E0B]">{tasks.filter((task) => task.type === "recurring").length}/{recurringLimit}</span> on Free · one-time tasks are unlimited</>
                )}
              </div>

              {showAdd && <TaskForm onSubmit={submit} onCancel={() => setShowAdd(false)} showOwner={false} stakesEnabled={stakesEnabled} />}

              {loading ? <div className="text-sm text-[#A1A1AA] italic py-6">Loading tasks...</div> : list.length === 0 ? (
                <div className="text-sm text-[#A1A1AA] italic py-6" data-testid="tasks-empty">
                  {view === "today" ? "Nothing scheduled today. Add a task above." : "No tasks yet. Create your first."}
                </div>
              ) : (
                <ul className="space-y-2">
                  {list.map((t) => (
                    <TaskRow
                      key={t.id} task={t} mode="self"
                      stakesEnabled={stakesEnabled}
                      onToggle={tog} onDelete={del}
                      extraActions={(task) => <>
                        {!task.doneToday && !task.done && <Link to={`/tools/focus?task=${encodeURIComponent(task.id)}`} className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-[#F5F5F0]" data-testid={`task-focus-${task.id}`}><Timer className="h-3 w-3" /> Focus</Link>}
                        {active && canUseWorkspace && !importedSourceIds.has(task.id) ? <button onClick={() => importOne(task.id)} data-testid={`task-import-${task.id}`} className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:border-[#F59E0B]/40 hover:text-[#F5F5F0]" title="Import to partnership workspace"><Activity className="h-3 w-3" /> Import</button> : active && canUseWorkspace && importedSourceIds.has(task.id) ? <span className="rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-[#F59E0B]" title="Already in partnership">In workspace</span> : null}
                      </>}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {active && partner ? (
              <div className="hm-card p-5 rounded-md">
                <div className="overline mb-2">Partnership</div>
                <div className="text-sm text-[#F5F5F0]">Active with <span className="text-[#F59E0B]">{partner.fullName.split(" ")[0]}</span></div>
                <p className="text-xs text-[#A1A1AA] font-light mt-2 mb-3">
                  Imported tasks show up in your workspace and are visible to your partner. Toggle done here or there — both sync.
                </p>
                <Link to={`/workspace/${active.id}?tab=tasks`} className="text-[10px] text-[#F59E0B] uppercase tracking-[0.2em] inline-flex items-center gap-1">
                  Open workspace <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ) : (
              <div className="hm-card p-5 rounded-md">
                <div className="overline mb-2">No partner yet</div>
                <p className="text-xs text-[#A1A1AA] font-light">
                  Your tasks are yours — solo. Find a partner to add real accountability.
                </p>
                <Link to="/discover" className="mt-3 inline-flex items-center gap-1 text-[10px] text-[#F59E0B] uppercase tracking-[0.2em]">
                  Discover <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            )}

            <div className="hm-card p-5 rounded-md">
              <Heatmap
                data={completionMap}
                weeks={26}
                cellSize={14}
                title="Your consistency"
                subtitle="Last 6 months · task completions per day"
                testId="tasks-heatmap"
              />
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function updateHeatmap(current, previousTask, updatedTask) {
  const date = todayISO();
  const wasComplete = (previousTask?.completions || []).includes(date);
  const isComplete = (updatedTask.completions || []).includes(date);
  if (wasComplete === isComplete) return current;
  const value = Math.max(0, (current[date] || 0) + (isComplete ? 1 : -1));
  return { ...current, [date]: value };
}

function StatPill({ label, value }) {
  return (
    <div className="hm-card px-4 py-3 rounded-md flex items-center gap-3">
      <Sparkles className="w-4 h-4 text-[#F59E0B]" strokeWidth={1.5} />
      <div>
        <div className="font-serif-editorial text-xl leading-none">{value}</div>
        <div className="text-[10px] text-[#A1A1AA] uppercase tracking-[0.18em] mt-0.5">{label}</div>
      </div>
    </div>
  );
}
